/**
 * Part D — smarter Ollie “Compare suppliers” landing on Catalogue.
 *
 * Compare used to search the full branded product title. Typesense requires
 * every token to match, so long titles often return nothing while a short
 * catalogue search (e.g. “tomato”) returns hundreds. We build a tighter
 * comparable query, keep the original product as context, and never claim
 * loose hits are confirmed substitutes.
 */

import { resolvePackSizeFromProductSize } from './priceFunctions'

export const OLLIE_COMPARE_COPY = Object.freeze({
  bannerLead: 'Comparing suppliers for',
  bannerHint:
    'Suggestions only — review pack size and description before switching. These are not confirmed substitutes.',
  empty:
    "I couldn't find a directly comparable alternative from your available suppliers.",
  searchRelated: 'Search related products',
  yourProduct: 'Your product',
  comparablePrice: 'Approx. £ / pack unit',
  comparablePriceHint:
    'Shown only when pack size can be read reliably. Total pack price may still differ.',
})

/** Words that rarely identify the product type. */
const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'of',
  'for',
  'with',
  'from',
  'by',
  'to',
  'in',
  'on',
  'at',
  'pack',
  'packs',
  'case',
  'cases',
  'box',
  'boxes',
  'tray',
  'trays',
  'bag',
  'bags',
  'each',
  'approx',
  'approximately',
  'new',
  'fresh',
])

/** Strip pack / weight tokens so Typesense is not forced to match them. */
const SIZE_PATTERN =
  /\b\d+(?:[.,]\d+)?\s*(?:x|×)\s*\d+(?:[.,]\d+)?\s*(?:kg|g|ml|cl|l|ltr|litre|liter|oz|lb|lbs)?\b|\b\d+(?:[.,]\d+)?\s*(?:kg|g|ml|cl|l|ltr|litre|liter|oz|lb|lbs)\b|\b\d+\s*(?:pack|pk|pc|pcs|piece|pieces)\b/gi

const cleanTitle = value =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()

const tokensOf = title =>
  cleanTitle(title)
    .replace(SIZE_PATTERN, ' ')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .map(token => token.trim())
    .filter(token => token.length >= 2 && !STOP_WORDS.has(token))

/**
 * Comparable catalogue query from a full product title.
 * Prefers the distinctive product words (usually after the brand), not the
 * whole branded string Typesense would require token-for-token.
 */
export function buildOllieCompareSearchTerm(productTitle) {
  const tokens = tokensOf(productTitle)
  if (!tokens.length) {
    const fallback = cleanTitle(productTitle)
    return fallback || null
  }
  if (tokens.length <= 2) return tokens.join(' ')
  if (tokens.length === 3) return tokens.slice(-2).join(' ')
  // Brand + descriptors at the front; keep the last three content words.
  return tokens.slice(-3).join(' ')
}

/**
 * Broader related search when no comparable alternative is found.
 * One strong keyword so the standard catalogue search can explore.
 */
export function buildOllieRelatedSearchTerm(productTitle) {
  const tokens = tokensOf(productTitle)
  if (!tokens.length) {
    const fallback = cleanTitle(productTitle).split(/\s+/)[0]
    return fallback || null
  }
  // Prefer a mid/late content word (e.g. tomato from “Cirio … Tomato Puree”).
  const pick =
    tokens.length >= 2 ? tokens[tokens.length - 2] : tokens[tokens.length - 1]
  return pick || tokens[0]
}

/** e.g. "6 x 400g" / "12×400g" — pack count is a reliable unit divisor. */
const MULTI_PACK_PATTERN = /\d+(?:[.,]\d+)?\s*[x×X]\s*\d+(?:[.,]\d+)?/

/**
 * Approx £ per pack unit when size is a multi-pack we can divide reliably.
 * Null when we would mislead (no price, single tin/weight, or unparseable).
 */
export function catalogueComparableUnitPrice(product = {}) {
  const price = Number(product.price ?? product.OMSPrice)
  if (!Number.isFinite(price) || price <= 0) return null
  const sizeRaw = String(product.size || '').trim()
  if (!sizeRaw || !MULTI_PACK_PATTERN.test(sizeRaw)) return null
  const pack = resolvePackSizeFromProductSize(sizeRaw)
  if (!Number.isFinite(pack) || pack <= 1) return null
  return price / pack
}

export function formatCatalogueComparableUnitPrice(product) {
  const unit = catalogueComparableUnitPrice(product)
  if (unit == null) return null
  return `£${unit.toFixed(2)}`
}

/** Results that are not the risen product itself. */
export function filterOllieCompareAlternatives(
  products = [],
  { compareProductId = null, compareProductTitle = null } = {},
) {
  const id = compareProductId ? String(compareProductId) : null
  const title = compareProductTitle
    ? String(compareProductTitle).trim().toLowerCase()
    : null
  return (products || []).filter(product => {
    if (id && String(product?._id || product?.id || '') === id) return false
    if (
      title &&
      String(product?.title || '')
        .trim()
        .toLowerCase() === title
    ) {
      return false
    }
    return true
  })
}
